/**
 * Felles meldingsmodell for Chat-rommet (VIB-3022). Rommeldinger
 * (`chat_messages`) og prosjektoppdateringer (`project_comments`) mappes til
 * samme form, så meldingslisten ikke trenger å vite hvor de kommer fra.
 */

export interface ChannelAttachment {
  url: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number | null;
}

export interface ChannelMessage {
  id: string;
  /** `user` er skrevet av en person, `system` er en hendelse fra Revehiet. */
  kind: "user" | "system";
  authorId: string | null;
  authorName: string;
  authorAvatarUrl: string | null;
  body: string;
  createdAt: string;
  mentionedUserIds: string[];
  attachments: ChannelAttachment[];
  /** 👍 på prosjektoppdateringer (project_comment_likes). Tom for rommeldinger. */
  likedByUserIds: string[];
  /** Lenke-brikke under en systemmelding (VIB-3026). */
  link?: { kind: string; label: string; href: string };
}

export interface ChatMessageRow {
  id: string;
  sender_id: string;
  body: string | null;
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  attachment_size_bytes: number | null;
  created_at: string;
  mentioned_user_ids?: string[] | null;
  sender?: { full_name: string; avatar_url: string | null } | null;
}

export interface ProjectCommentRow {
  id: string;
  author_id: string;
  body: string;
  created_at: string;
  mentioned_user_ids: string[] | null;
  author?: { full_name: string; avatar_url: string | null } | null;
  attachments?: { name: string; url: string; file_size: number | null }[];
  liked_by_user_ids?: string[];
}

export function fromChatMessage(m: ChatMessageRow): ChannelMessage {
  return {
    id: m.id,
    kind: "user",
    authorId: m.sender_id,
    authorName: m.sender?.full_name ?? "Ukjent",
    authorAvatarUrl: m.sender?.avatar_url ?? null,
    body: m.body ?? "",
    createdAt: m.created_at,
    mentionedUserIds: m.mentioned_user_ids ?? [],
    attachments: m.attachment_url
      ? [{
          url: m.attachment_url,
          name: m.attachment_name ?? "Vedlegg",
          mimeType: m.attachment_type,
          sizeBytes: m.attachment_size_bytes,
        }]
      : [],
    likedByUserIds: [],
  };
}

export function fromProjectComment(c: ProjectCommentRow): ChannelMessage {
  return {
    id: c.id,
    kind: "user",
    authorId: c.author_id,
    authorName: c.author?.full_name ?? "Ukjent",
    authorAvatarUrl: c.author?.avatar_url ?? null,
    body: c.body,
    createdAt: c.created_at,
    mentionedUserIds: c.mentioned_user_ids ?? [],
    attachments: (c.attachments ?? []).map((a) => ({
      url: a.url,
      name: a.name,
      mimeType: null,
      sizeBytes: a.file_size,
    })),
    likedByUserIds: c.liked_by_user_ids ?? [],
  };
}

/** Eldst først. Meldinger med samme tidspunkt holder rekkefølgen sin. */
export function sortMessages(messages: ChannelMessage[]): ChannelMessage[] {
  return [...messages].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * Indeksen der «N nye»-streken skal stå: før den eldste av de `unread`
 * siste meldingene som andre har skrevet. Systemmeldinger og egne meldinger
 * teller ikke som uleste. -1 når ingen er uleste.
 */
export function firstUnreadIndex(messages: ChannelMessage[], unread: number, selfId: string | null): number {
  if (unread <= 0) return -1;
  let remaining = unread;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.kind === "user" && m.authorId !== selfId) {
      remaining--;
      if (remaining === 0) return i;
    }
  }
  return messages.findIndex((m) => m.kind === "user" && m.authorId !== selfId);
}

export interface BodyPart {
  text: string;
  /** Satt når delen er en @-nevning av en kjent person. */
  mentionUserId?: string;
}

/**
 * Deler en meldingstekst i vanlig tekst og @-nevninger. Nevninger lagres som
 * «@Fullt Navn» i teksten pluss ID i `mentioned_user_ids` (samme format som
 * Prosjektoppdateringer), så vi slår opp navnene til de nevnte.
 */
export function splitMentions(
  body: string,
  mentioned: { id: string; full_name: string }[],
): BodyPart[] {
  const names = mentioned
    .filter((u) => u.full_name)
    .sort((a, b) => b.full_name.length - a.full_name.length);
  if (names.length === 0) return body ? [{ text: body }] : [];

  const parts: BodyPart[] = [];
  let buffer = "";
  let i = 0;
  while (i < body.length) {
    if (body[i] === "@") {
      const hit = names.find((u) => body.startsWith(u.full_name, i + 1));
      if (hit) {
        if (buffer) parts.push({ text: buffer });
        buffer = "";
        parts.push({ text: `@${hit.full_name}`, mentionUserId: hit.id });
        i += hit.full_name.length + 1;
        continue;
      }
    }
    buffer += body[i];
    i++;
  }
  if (buffer) parts.push({ text: buffer });
  return parts;
}

/** «14.52» i dag, «I går 14.52», ellers «23.10 14.52». */
export function formatMessageTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" }).replace(":", ".");
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (diffDays === 0) return time;
  if (diffDays === 1) return `I går ${time}`;
  const date = `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
  return d.getFullYear() === now.getFullYear() ? `${date} ${time}` : `${date}.${d.getFullYear()} ${time}`;
}

interface ThreadedComment extends ProjectCommentRow {
  replies?: ThreadedComment[];
}

interface CommentDocument {
  comment_id: string | null;
  file_name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
}

/**
 * Prosjektoppdateringer som kanalmeldinger. Svar i tråder flates ut i tid,
 * fordi Chat-rommet viser én strøm. Dokumenter lastet opp fra skrivefeltet
 * (`project_documents.comment_id`) legges på meldingen de hører til.
 */
export function projectMessagesFrom(
  comments: ThreadedComment[],
  documents: CommentDocument[],
  urlFor: (filePath: string) => string,
): ChannelMessage[] {
  const docsByComment = new Map<string, CommentDocument[]>();
  for (const d of documents) {
    if (!d.comment_id) continue;
    docsByComment.set(d.comment_id, [...(docsByComment.get(d.comment_id) ?? []), d]);
  }
  const flat: ThreadedComment[] = [];
  const walk = (list: ThreadedComment[]) => {
    for (const c of list) {
      flat.push(c);
      if (c.replies?.length) walk(c.replies);
    }
  };
  walk(comments);
  return sortMessages(
    flat.map((c) => {
      const m = fromProjectComment(c);
      const docs = docsByComment.get(c.id) ?? [];
      return {
        ...m,
        attachments: [
          ...m.attachments,
          ...docs.map((d) => ({ url: urlFor(d.file_path), name: d.file_name, mimeType: d.mime_type, sizeBytes: d.file_size })),
        ],
      };
    }),
  );
}
